import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Container, Alert, CircularProgress, Chip } from '@mui/material';
import { ArrowBack, LocationOn, Phone } from '@mui/icons-material';
import { 
  useAppointmentsRetrieveQuery, 
  useAppointmentsPartialUpdateMutation,
  useSalonsRetrieveQuery, 
  useServicesPublicSalonListQuery,
  useTeamsTeamMembersSalonMembersRetrieveQuery
} from '../../../services/GLOWClient';
import DateTimeSelectionStep from '../salonDetails/DateTimeSelectionStep';
import { theme } from '../../../theme';
import { BASE_URL } from '../../../config';

// Helper function to build image URL
const buildImageUrl = (url) => {
  if (!url) return '';
  
  if (/^https?:\/\//i.test(url)) {
    if (url.match(/^http:\/\/localhost\//) && !url.match(/^http:\/\/localhost:[0-9]+\//)) {
      return url.replace('http://localhost/', 'http://localhost:8012/');
    }
    return url;
  }
  
  if (url.startsWith('/media/')) {
    const baseUrl = window.location.hostname === 'localhost' ? 
      'http://localhost:8012' : 
      BASE_URL.replace(/\/$/, '');
    return `${baseUrl}${url}`;
  }
  
  if (url.startsWith('media/')) {
    const baseUrl = window.location.hostname === 'localhost' ? 
      'http://localhost:8012' : 
      BASE_URL.replace(/\/$/, '');
    return `${baseUrl}/${url}`;
  }
  
  return url;
};

const UpdateAppointment = () => {
  const { salonId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const updateAppointmentId = searchParams.get('updateAppointment');

  // Fetch appointment data for updating
  const { data: updateAppointmentData, isLoading: appointmentLoading, error: appointmentError } = useAppointmentsRetrieveQuery(
    { appointmentId: updateAppointmentId },
    { skip: !updateAppointmentId }
  );

  // Fetch salon details
  const { data: salonDetails, isLoading: salonLoading } = useSalonsRetrieveQuery(
    { salonId },
    { skip: !salonId }
  );

  // Fetch services to find the selected service
  const { data: allServices, isLoading: servicesLoading } = useServicesPublicSalonListQuery(
    { salonId },
    { skip: !salonId }
  );

  // Fetch salon members (employees) for DateTimeSelectionStep
  const { data: salonMembersData, isLoading: membersLoading } = useTeamsTeamMembersSalonMembersRetrieveQuery(
    salonId ? { salon: salonId } : {},
    { skip: !salonId }
  );

  // Mutation for updating appointment
  const [updateAppointment, { isLoading: isUpdating }] = useAppointmentsPartialUpdateMutation();

  // Find the selected service from appointment data
  const selectedService = React.useMemo(() => {
    if (!updateAppointmentData || !allServices) return null;
    return allServices.find(service => service.service_id === updateAppointmentData.service);
  }, [updateAppointmentData, allServices]);

  const handleBack = () => {
    navigate('/client/salons');
  };

  const handleSuccess = async (appointmentData) => {
    try {
      
      // Handle slot selection data from AppointmentCalendar
      let dateStr, timeStr, teamMemberId, serviceId;
      
      if (appointmentData && appointmentData.selectedDate && appointmentData.selectedTime) {
        // Direct slot selection data from update mode
        dateStr = appointmentData.selectedDate; // YYYY-MM-DD
        timeStr = appointmentData.selectedTime; // HH:MM
        teamMemberId = appointmentData.teamMember;
        serviceId = appointmentData.serviceId;
      } else if (appointmentData && appointmentData.start_time) {
        // Fallback: appointment data with start_time (from successful booking)
        const startTime = new Date(appointmentData.start_time);
        dateStr = startTime.toISOString().split('T')[0]; // YYYY-MM-DD
        timeStr = startTime.toTimeString().split(' ')[0].substring(0, 5); // HH:MM
        teamMemberId = appointmentData.team_member;
        serviceId = appointmentData.service;
      } else {
        console.error('Invalid appointment data received:', appointmentData);
        return;
      }
      
      
      // Calculate end time based on service duration
      const serviceDuration = selectedService?.duration || 60; // Default to 60 minutes
      
      // Parse the selected date and time
      const startDateTime = new Date(`${dateStr}T${timeStr}:00`);
      
      // Calculate end_time by adding service duration
      const endDateTime = new Date(startDateTime.getTime() + serviceDuration * 60000);
      
      // Format dates correctly without timezone conversion
      const formatLocalDateTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };

      const start_time = formatLocalDateTime(startDateTime);
      const end_time = formatLocalDateTime(endDateTime);
      
      // Prepare update data - only include changed fields
      const updateData = {
        start_time: start_time,
        end_time: end_time
      };

      // Include team member if it changed
      if (teamMemberId && teamMemberId !== updateAppointmentData.team_member) {
        updateData.team_member = teamMemberId;
      }

      // Include service if it changed
      if (serviceId && serviceId !== updateAppointmentData.service) {
        updateData.service = serviceId;
      }
      
      // Update the appointment using partial update
      await updateAppointment({
        appointmentId: updateAppointmentId,
        patchedAppointmentUpdateRequest: updateData
      }).unwrap();

      
      // Navigate back to appointments after successful update with refresh
      navigate('/client/appointments', { replace: true });
      // Force a page refresh to ensure data is updated
      window.location.reload();
    } catch (error) {
      console.error('Failed to update appointment:', error);
      // Handle error - could show a snackbar or alert
    }
  };

  if (appointmentLoading || salonLoading || servicesLoading || membersLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Chargement...</Typography>
        </Box>
      </Container>
    );
  }

  if (appointmentError || !updateAppointmentData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ mb: 2 }}
          >
            Retour aux rendez-vous
          </Button>
        </Box>
        <Alert severity="error">
          Erreur lors du chargement des données du rendez-vous.
        </Alert>
      </Container>
    );
  }

  if (!selectedService) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ mb: 2 }}
          >
            Retour aux rendez-vous
          </Button>
        </Box>
        <Alert severity="error">
          Service non trouvé pour ce rendez-vous.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header with background image */}
      <Box 
        sx={{ 
          position: 'relative',
          minHeight: { xs: '10px', sm: '70px', md: '100px' },
          borderRadius: 3,
          overflow: 'hidden',
          mb: 4,
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
        }}
      >
        {/* Background Image */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: salonDetails?.photos && salonDetails.photos.length > 0
              ? `url(${buildImageUrl(typeof salonDetails.photos[0] === 'string' ? salonDetails.photos[0] : salonDetails.photos[0]?.image || salonDetails.photos[0]?.url || '')})`
              : `linear-gradient(135deg, ${theme.colors.primary[400]} 0%, ${theme.colors.secondary[400]} 100%)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.7)',
            borderRadius: 3,
          }}
        />
        
        {/* Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(74, 124, 89, 0.85) 0%, rgba(139, 69, 19, 0.85) 100%)',
            borderRadius: 3,
          }}
        />
        
        {/* Content */}
        <Box
          sx={{
            position: 'relative',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: { xs: 2, sm: 4, md: 5 },
            pt: { xs: 6, sm: 4, md: 5 },
            color: 'white',
          }}
        >
          {/* Back Button */}
          <Button 
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ 
              position: 'absolute',
              top: { xs: 12, sm: 24 },
              left: { xs: 12, sm: 24 },
              color: 'white',
              fontWeight: 600,
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              px: { xs: 2, sm: 3 },
              py: { xs: 0.75, sm: 1 },
              borderRadius: 2,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                transform: 'translateX(-4px)',
                transition: 'all 0.2s'
              }
            }}
          >
            Retour
          </Button>
          
          {/* Salon Name */}
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 800,
              textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
              fontSize: { xs: '1.5rem', sm: '2.25rem', md: '2.75rem' },
              mb: 1,
              textAlign: 'center',
              mt: { xs: 2, sm: 0 }
            }}
          >
            {salonDetails?.name || 'Salon'}
          </Typography>
          
          {/* Subtitle */}
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 500,
              textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
              fontSize: { xs: '1rem', sm: '1.25rem' },
              mb: 3,
              textAlign: 'center',
              opacity: 0.95
            }}
          >
            Modifier votre rendez-vous
          </Typography>
          
          {/* Appointment Info Card with Glassmorphism */}
          <Box 
            sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(20px)',
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.2)',
              p: 1,
              maxWidth: '600px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              mx: 'auto',
              textAlign: 'center',
              mb: 1
            }}
          >
            {/* Label "Rendez-vous actuel" */}
            <Typography 
              variant="overline" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontWeight: 600,
                letterSpacing: '1px',
                fontSize: '0.75rem',
                display: 'block',
                mb: 1
              }}
            >
              Rendez-vous actuel
            </Typography>
            
            {/* Service Name */}
            <Typography 
              variant="h5" 
              sx={{ 
                color: 'white',
                fontWeight: 700,
                mb: 1,
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              {selectedService?.name || 'Service'}
            </Typography>
            
            {/* Date and Time Row */}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
              {updateAppointmentData?.start_time && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '1rem'
                    }}
                  >
                    📅 {new Date(updateAppointmentData.start_time).toLocaleDateString('fr-FR', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </Typography>
                </Box>
              )}
              
              {updateAppointmentData?.start_time && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '1rem'
                    }}
                  >
                    🕐 {new Date(updateAppointmentData.start_time).toLocaleTimeString('fr-FR', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          
          {/* Address and Phone */}
          {(salonDetails?.address || salonDetails?.phone) && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              {salonDetails.address && (
                <Chip
                  icon={<LocationOn sx={{ color: 'white !important' }} />}
                  label={salonDetails.address}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    color: 'white',
                    fontWeight: 500,
                    '& .MuiChip-icon': {
                      color: 'white'
                    }
                  }}
                />
              )}
              {salonDetails.phone && (
                <Chip
                  icon={<Phone sx={{ color: 'white !important' }} />}
                  label={salonDetails.phone}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    color: 'white',
                    fontWeight: 500,
                    '& .MuiChip-icon': {
                      color: 'white'
                    }
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Loading indicator for update operation */}
      {isUpdating && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={20} sx={{ mr: 2 }} />
            Mise à jour du rendez-vous en cours...
          </Alert>
        </Box>
      )}

      {/* Date/Time Selection (same UI as booking) */}
      <DateTimeSelectionStep
        salonId={salonId}
        salonDetails={salonDetails}
        employees={Array.isArray(salonMembersData) ? salonMembersData : (salonMembersData?.results || [])}
        selectedServices={[{
          service_id: selectedService?.service_id,
          duration: selectedService?.duration,
          price: selectedService?.price,
          teamMember: updateAppointmentData?.team_member || 'any'
        }]}
        onBack={handleBack}
        onCancel={handleBack}
        onContinue={({ date, slot }) => {
          const selectedTime = typeof slot === 'object' ? slot.start_time : slot;
          return handleSuccess({
            selectedDate: date,
            selectedTime,
            teamMember: updateAppointmentData?.team_member,
            serviceId: selectedService?.service_id,
          });
        }}
      />
    </Container>
  );
};

export default UpdateAppointment;
